"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Calendar, MapPin, Users, Trophy, Medal, Plus, Edit, Trash2, Check, X } from "lucide-react"
import type { Competition, Fencer, CompetitionParticipation, CompetitionResult, AgeCategory } from "../types"
import { api } from "../services/api"
import { getAgeCategoryForDate } from "../utils/dateUtils"
import { Switch } from "../components/Switch"

export const CompetitionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [competition, setCompetition] = useState<Competition | null>(null)
  const [fencers, setFencers] = useState<Fencer[]>([])
  const [participations, setParticipations] = useState<CompetitionParticipation[]>([])
  const [results, setResults] = useState<CompetitionResult[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<CompetitionParticipation | CompetitionResult | null>(null)
  const [formData, setFormData] = useState({
    fencerId: "",
    willParticipate: true,
    ageCategory: "" as AgeCategory | "",
    notes: "",
    placement: "",
    totalParticipants: "",
  })

  useEffect(() => {
    if (id) {
      loadData(id)
    }
  }, [id])

  const loadData = async (competitionId: string) => {
    try {
      const [competitionData, fencersData, participationsData, resultsData] = await Promise.all([
        api.getCompetition(competitionId),
        api.getFencers(),
        api.getCompetitionParticipations(competitionId),
        api.getCompetitionResults(competitionId),
      ])

      if (!competitionData) {
        navigate("/competitions")
        return
      }

      setCompetition(competitionData)
      setFencers(fencersData)
      setParticipations(participationsData)
      setResults(resultsData)
    } catch (error) {
      console.error("Error loading competition details:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!id || !formData.fencerId || !formData.ageCategory) return

    try {

      // Handle participation part
      if (editingItem && "willParticipate" in editingItem) {
        // Updating existing participation
        await api.updateCompetitionParticipation(editingItem.id, {
          willParticipate: formData.willParticipate,
          ageCategory: formData.ageCategory as AgeCategory,
          notes: formData.notes,
        })
      } else {
        // Creating new participation
        await api.addCompetitionParticipation({
          competitionId: id,
          fencerId: formData.fencerId,
          willParticipate: formData.willParticipate,
          ageCategory: formData.ageCategory as AgeCategory,
          notes: formData.notes,
        })
      }

      // Only handle result part if both placement and totalParticipants are filled out
      const placement = Number.parseInt(formData.placement)
      const totalParticipants = Number.parseInt(formData.totalParticipants)

      if (
        formData.placement &&
        formData.totalParticipants &&
        placement > 0 &&
        totalParticipants > 0 &&
        formData.willParticipate
      ) {
        if (editingItem && "placement" in editingItem) {
          // Updating existing result
          await api.updateCompetitionResult(editingItem.id, {
            ageCategory: formData.ageCategory as AgeCategory,
            placement: placement,
            totalParticipants: totalParticipants,
            notes: formData.notes,
          })
        } else {
          // Check if result already exists
          const existingResults = await api.getCompetitionResults(id, formData.fencerId)
          const existingResult = existingResults.find((r) => r.ageCategory === formData.ageCategory)

          if (existingResult) {
            // Update existing result
            await api.updateCompetitionResult(existingResult.id, {
              placement: placement,
              totalParticipants: totalParticipants,
              notes: formData.notes,
            })
          } else {
            // Create new result
            await api.addCompetitionResult({
              competitionId: id,
              fencerId: formData.fencerId,
              ageCategory: formData.ageCategory as AgeCategory,
              placement: placement,
              totalParticipants: totalParticipants,
              notes: formData.notes,
            })
          }
        }
      }

      await loadData(id)
      resetForm()
    } catch (error) {
      console.error("Error saving data:", error)
    }
  }

  const handleEditParticipation = (participation: CompetitionParticipation) => {
    const existingResult = results.find(
      (r) => r.fencerId === participation.fencerId && r.ageCategory === participation.ageCategory,
    )

    setEditingItem(participation)
    setFormData({
      fencerId: participation.fencerId,
      willParticipate: participation.willParticipate,
      ageCategory: participation.ageCategory || "",
      notes: participation.notes || "",
      placement: existingResult?.placement.toString() || "",
      totalParticipants: existingResult?.totalParticipants.toString() || "",
    })
    setShowForm(true)
  }

  const handleEditResult = (result: CompetitionResult) => {
    const participation = participations.find(
      (p) => p.fencerId === result.fencerId && p.ageCategory === result.ageCategory,
    )

    if (!participation) return

    setEditingItem(result)
    setFormData({
      fencerId: result.fencerId,
      willParticipate: true,
      ageCategory: result.ageCategory,
      notes: result.notes || "",
      placement: result.placement.toString(),
      totalParticipants: result.totalParticipants.toString(),
    })
    setShowForm(true)
  }

  const handleDeleteParticipation = async (participationId: string) => {
    if (!confirm("Are you sure you want to remove this participant?")) return

    try {
      await api.deleteCompetitionParticipation(participationId)
      await loadData(id!)
    } catch (error) {
      console.error("Error deleting participation:", error)
    }
  }

  const handleDeleteResult = async (resultId: string) => {
    if (!confirm("Are you sure you want to delete this result?")) return

    try {
      await api.deleteCompetitionResult(resultId)
      await loadData(id!)
    } catch (error) {
      console.error("Error deleting result:", error)
    }
  }

  const resetForm = () => {
    setFormData({
      fencerId: "",
      willParticipate: true,
      ageCategory: "",
      notes: "",
      placement: "",
      totalParticipants: "",
    })
    setEditingItem(null)
    setShowForm(false)
  }

  const handleFencerChange = (fencerId: string) => {
    if (!competition) return

    const fencer = fencers.find((f) => f.id === fencerId)
    if (!fencer) return

    const suggestedCategory = getAgeCategoryForDate(fencer.dateOfBirth, competition.date) as AgeCategory

    setFormData((prev) => ({
      ...prev,
      fencerId,
      ageCategory: competition.ageCategories.includes(suggestedCategory)
        ? suggestedCategory
        : competition.ageCategories[0],
    }))
  }

  const getPlacementColor = (placement: number, total: number) => {
    const percentage = placement / total
    if (placement === 1) return "text-yellow-600 dark:text-yellow-400" // Gold
    if (placement === 2) return "text-gray-500 dark:text-gray-400" // Silver
    if (placement === 3) return "text-orange-600 dark:text-orange-400" // Bronze
    if (percentage <= 0.1) return "text-green-600 dark:text-green-400" // Top 10%
    if (percentage <= 0.25) return "text-blue-600 dark:text-blue-400" // Top 25%
    return "text-gray-600 dark:text-gray-300"
  }

  const participatingFencers = participations.filter((p) => p.willParticipate)

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading competition details...</span>
      </div>
    )
  }

  if (!competition) {
    return (
      <div className="text-center py-12">
        <Trophy className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Competition not found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">The requested competition could not be found.</p>
        <button
          onClick={() => navigate("/competitions")}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          Back to Competitions
        </button>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <button
          onClick={() => navigate("/competitions")}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Competitions
        </button>
      </div>

      {/* Competition Header */}
      <div className="card p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white break-words">
              {competition.name}
            </h1>
            <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:items-center text-sm text-gray-500 dark:text-gray-400 space-y-1 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="break-words">{competition.date}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="break-words">{competition.location}</span>
              </div>
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="break-words">{competition.ageCategories.join(", ")}</span>
              </div>
            </div>
            {competition.notes && (
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 break-words">{competition.notes}</p>
            )}
          </div>
          <div className="flex items-center space-x-2 mt-4 sm:mt-0 sm:ml-4 flex-shrink-0">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
              {participatingFencers.length} Participants
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
              {results.length} Results
            </span>
          </div>
        </div>
      </div>

      {/* Add Participant Button */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Participants & Results</h2>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Participant
        </button>
      </div>

      {/* Combined Form */}
      {showForm && (
        <div className="card p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {editingItem ? "Edit Participant" : "Add Participant"}
          </h3>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fencer *</label>
                <select
                  value={formData.fencerId}
                  onChange={(e) => handleFencerChange(e.target.value)}
                  required
                  disabled={!!editingItem}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                >
                  <option value="">Select a fencer</option>
                  {fencers.map((fencer) => (
                    <option key={fencer.id} value={fencer.id}>
                      {fencer.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Age Category *
                </label>
                <select
                  value={formData.ageCategory}
                  onChange={(e) => setFormData((prev) => ({ ...prev, ageCategory: e.target.value as AgeCategory }))}
                  required
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                >
                  <option value="">Select category</option>
                  {competition.ageCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Participation</label>
              </div>
              <Switch
                checked={formData.willParticipate}
                onChange={(checked) => setFormData((prev) => ({ ...prev, willParticipate: checked }))}
                label={formData.willParticipate ? "Will participate" : "Will not participate"}
              />
            </div>

            {formData.willParticipate && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  Competition Result (Optional)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Placement</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.placement}
                      onChange={(e) => setFormData((prev) => ({ ...prev, placement: e.target.value }))}
                      placeholder="Enter placement (optional)"
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Total Participants
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.totalParticipants}
                      onChange={(e) => setFormData((prev) => ({ ...prev, totalParticipants: e.target.value }))}
                      placeholder="Enter total participants (optional)"
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Leave placement and total participants empty to add participant without result. You can add the result
                  later.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes</label>
              <textarea
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                placeholder="Add any notes..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 border border-transparent rounded-md"
              >
                {editingItem ? "Update" : "Add"} Participant
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Participants Section */}
      <div className="card overflow-hidden mb-6">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Participants ({participations.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Fencer
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Category
                </th>
                <th className="px-4 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Status
                </th>
                <th className="relative px-4 sm:px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {participations.map((participation) => {
                const fencer = fencers.find((f) => f.id === participation.fencerId)
                return (
                  <tr key={participation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {fencer?.name || "Unknown Fencer"}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {participation.ageCategory}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-center">
                      {participation.willParticipate ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                          <Check className="h-3 w-3 mr-1" />
                          Participating
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                          <X className="h-3 w-3 mr-1" />
                          Not Participating
                        </span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditParticipation(participation)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3 p-1 rounded"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteParticipation(participation.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {participations.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No participants yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add fencers who will participate in this competition.
            </p>
          </div>
        )}
      </div>

      {/* Results Section */}
      <div className="card overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Results ({results.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Fencer
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Category
                </th>
                <th className="px-4 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  Result
                </th>
                <th className="relative px-4 sm:px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {results.map((result) => {
                const fencer = fencers.find((f) => f.id === result.fencerId)
                const placementColor = getPlacementColor(result.placement, result.totalParticipants)
                return (
                  <tr key={result.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {fencer?.name || "Unknown Fencer"}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {result.ageCategory}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        {result.placement <= 3 && <Medal className={`h-4 w-4 ${placementColor}`} />}
                        <span className={`text-sm font-semibold ${placementColor}`}>
                          {result.placement} / {result.totalParticipants}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditResult(result)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3 p-1 rounded"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteResult(result.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {results.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No results yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add results for participants in this competition.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
